import type { EmptyStateOnboardingCallout, PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { ExternalLink, SearchInput } from "@octopusdeploy/design-system-components";
import type { EnvironmentsSummaryResource, TenantResource, EnvironmentV2Resource, Repository } from "@octopusdeploy/octopus-server-client";
import { getStaticAndParentEnvironmentsDependingOnIfEphemeralEnvironmentsAreEnabled, Permission } from "@octopusdeploy/octopus-server-client";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import { isEqual } from "lodash";
import { useEffect } from "react";
import * as React from "react";
import endpointRegistry from "~/areas/infrastructure/components/MachineSettings/Endpoints/endpointRegistry";
import type { EndpointRegistration } from "~/areas/infrastructure/components/MachineSettings/Endpoints/endpointRegistry";
import ConfirmTentacleUpgradePanel from "~/areas/infrastructure/components/MachinesLayout/ConfirmTentacleUpgradePanel";
import { useOnClearMachineDispatch } from "~/areas/infrastructure/hooks/useOnClearMachineDispatch";
import { AdvancedFilters } from "~/components/AdvancedFilterLayout/AdvancedFilters";
import { AdvancedFilterCheckbox } from "~/components/AdvancedFilterLayout/index";
import { BorderedContent } from "~/components/BorderedContent/BorderedContent";
import { useLegacyDoBusyTask } from "~/components/DataBaseComponent/useLegacyDoBusyTask";
import { FeatureToggle, Feature } from "~/components/FeatureToggle";
import { PortalEmptyStateOnboarding } from "~/components/GettingStarted/PortalEmptyStateOnboarding";
import DeploymentTargetTypeMultiSelect from "~/components/MultiSelect/DeploymentTargetTypeMultiselect";
import { EnvironmentV2MultiSelect } from "~/components/MultiSelect/EnvironmentV2MultiSelect";
import { MachineModelHealthStatusMultiSelect } from "~/components/MultiSelect/MachineModelHealthStatusMultiSelect";
import { ShellNameMultiSelect } from "~/components/MultiSelect/ShellNameMultiSelect";
import { TargetTagMultiSelect } from "~/components/MultiSelect/TargetTagMultiSelect";
import { TenantMultiSelect } from "~/components/MultiSelect/TenantMultiSelect";
import { TenantTagMultiSelect } from "~/components/MultiSelect/TenantTagMultiSelect";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { PageContent } from "~/components/PageContent/PageContent";
import { PermissionCheck } from "~/components/PermissionCheck";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import * as tenantTagsets from "~/components/tenantTagsets";
import type { TagIndex } from "~/components/tenantTagsets";
import { useDebouncedExternalState } from "~/hooks/useDebouncedExternalState";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
import MachineHealthStatusHelper from "~/utils/MachineHealthStatusHelper/index";
import { defaultEnvironmentSummaryFilter, createEnvironmentSummaryArgs } from "../EnvironmentsLayout/EnvironmentSummaryFilter";
import type { EnvironmentSummaryFilter } from "../EnvironmentsLayout/EnvironmentSummaryFilter";
import DeploymentTargetsSummarySection from "./DeploymentTargetsSummarySection";
interface DeploymentTargetsPageProps {
    spaceId: string;
    loaderData: DeploymentTargetsPageLoaderData;
    queryParams: DeploymentTargetsPageQueryParams;
    setQueryParams: QueryParamValuesSetter<DeploymentTargetsPageQueryParams>;
}
export type DeploymentTargetsPageQueryParams = EnvironmentSummaryFilter;
export const deploymentTargetsPageTitle = "Deployment Targets";
interface DeploymentTargetsPageLoaderData {
    environments: EnvironmentV2Resource[];
    machineRoles: string[];
    tenants: TenantResource[];
    tagIndex: TagIndex;
    hasMachines: boolean;
    machineShellNames: string[];
    environmentsSummary: EnvironmentsSummaryResource;
    deploymentTargetRegistrations: EndpointRegistration[];
}
export async function deploymentTargetsPageLoader(repository: Repository, queryParams: DeploymentTargetsPageQueryParams, ephemeralEnvironmentsAreEnabled: boolean): Promise<DeploymentTargetsPageLoaderData> {
    const defaultArgs = await createEnvironmentSummaryArgs(queryParams);
    const environmentsSummary = repository.Environments.summary(defaultArgs);
    const hasMachines = repository.Machines.list({ take: 0 }).then((result) => result.TotalResults > 0);
    const environments = await getStaticAndParentEnvironmentsDependingOnIfEphemeralEnvironmentsAreEnabled(ephemeralEnvironmentsAreEnabled, repository);
    const machineRoles = repository.MachineRoles.all();
    const machineShellNames = repository.MachineShells.all();
    const tagIndex = tenantTagsets.getTagIndex();
    const targetRegistrations = endpointRegistry.getAllRegistrations();
    const tenants = isAllowed({ permission: Permission.TenantView, tenant: "*" }) ? await repository.Tenants.all() : [];
    return {
        environments: environments,
        machineRoles: await machineRoles,
        tenants,
        tagIndex: await tagIndex,
        machineShellNames: await machineShellNames,
        deploymentTargetRegistrations: await targetRegistrations,
        hasMachines: await hasMachines,
        environmentsSummary: await environmentsSummary,
    };
}
export function DeploymentTargetsPage({ spaceId, loaderData, queryParams, setQueryParams }: DeploymentTargetsPageProps) {
    const machineHealthStatuses = MachineHealthStatusHelper.getMachineModelHealthStatusResources();
    const summaries = loaderData.environmentsSummary;
    const onClearMachine = useOnClearMachineDispatch();
    const showV2Page = useOctopusFeatureToggle("deployment-targets-page-uplift", false);
    const { doBusyTask, status } = useLegacyDoBusyTask();
    const navigate = useSpaceAwareNavigation();
    const [debouncedPartialName, setDebouncedPartialName] = useDebouncedExternalState(queryParams.machinePartialName, (value) => setQueryParams((prev) => ({ ...prev, machinePartialName: value })), 200);
    const hasNoEnvironment = loaderData.environments.length === 0;
    useEffect(() => {
        onClearMachine();
    }, [onClearMachine]);
    const addDeploymentTargetPageAction: PrimaryPageAction = {
        type: "navigate",
        label: "Add Deployment Target",
        path: links.selectDeploymentTargetTypePage.generateUrl({ spaceId }),
        hasPermissions: isAllowed({
            permission: Permission.MachineCreate,
            environment: "*",
            tenant: "*",
        }),
        disabled: hasNoEnvironment,
        extraContext: hasNoEnvironment ? "Please create an environment before creating deployment target" : undefined,
    };
    const noEnvironmentOnboardingCallout: EmptyStateOnboardingCallout | undefined = loaderData.environments.length === 0
        ? {
            title: "Environments required",
            description: "Before you can create a deployment target, you will need to create at least one environment",
            action: {
                label: "Go to Environments",
                buttonType: "secondary",
                type: "navigate",
                path: links.environmentsPage.generateUrl({ spaceId }),
            },
        }
        : undefined;
    const tentacleUpgradesRequiredWarning = summaries.TentacleUpgradesRequired && (<ConfirmTentacleUpgradePanel doBusyTask={doBusyTask} onTentacleUpgradeComplete={() => {
            navigate.navigate(links.tasksPage.generateUrl());
        }}/>);
    if (!loaderData.hasMachines) {
        return (<PortalEmptyStateOnboarding title={"Configure your deployment targets"} description={"Deployment target represent the servers, machines and cloud services where software and services will be deployed."} primaryAction={addDeploymentTargetPageAction} learnMore={<>
                        <ExternalLink label={"Learn more"} href="OnboardingDeploymentTargetsLearnMore"/>
                        <ExternalLink label={"Video (5 mins)"} href={"OnboardingDeploymentTargetsVideo"}/>
                    </>} callout={noEnvironmentOnboardingCallout}/>);
    }
    const advancedFilters = (<AdvancedFilters>
            <AdvancedFilterCheckbox key="filterDisabled" label="Disabled only" value={queryParams.isDisabled ?? false} onChange={(x) => setQueryParams((prev) => ({ ...prev, isDisabled: x ? x : undefined }))}/>
            <EnvironmentV2MultiSelect accessibleName="environmentFilter" key="filterEnvironment" items={loaderData.environments} value={queryParams.environmentIds} onChange={(x) => setQueryParams((prev) => ({ ...prev, environmentIds: x }))}/>
            <TargetTagMultiSelect accessibleName="roleFilter" key="filterRole" items={loaderData.machineRoles ? loaderData.machineRoles : []} value={queryParams.roles} onChange={(x) => setQueryParams((prev) => ({ ...prev, roles: x }))}/>
            <FeatureToggle feature={Feature.MultiTenancy} key="filterMultiTenancy">
                <PermissionCheck permission={Permission.TenantView} tenant="*">
                    <TenantMultiSelect accessibleName="tenantFilter" value={queryParams.tenantIds} items={loaderData.tenants} onChange={(x) => setQueryParams((prev) => ({ ...prev, tenantIds: x }))}/>
                    <TenantTagMultiSelect value={queryParams.tenantTags} doBusyTask={doBusyTask} onChange={(x) => setQueryParams((prev) => ({ ...prev, tenantTags: x }))}/>
                </PermissionCheck>
            </FeatureToggle>
            <ShellNameMultiSelect accessibleName="shellNameFilter" key="filterShellName" items={loaderData.machineShellNames ?? []} value={queryParams.shellNames} onChange={(x) => setQueryParams((prev) => ({ ...prev, shellNames: x }))}/>
            <DeploymentTargetTypeMultiSelect accessibleName="deploymentTargetTypeFilter" key="filterDeploymentTargetType" items={loaderData.deploymentTargetRegistrations ?? []} value={queryParams.deploymentTargetTypes} onChange={(x) => setQueryParams((prev) => ({ ...prev, deploymentTargetTypes: x }))}/>
            <MachineModelHealthStatusMultiSelect accessibleName="healthStatusFilter" key="filterStatus" items={machineHealthStatuses} value={queryParams.healthStatuses} onChange={(x) => setQueryParams((prev) => ({ ...prev, healthStatuses: x }))}/>
        </AdvancedFilters>);
    const filters = {
        inputs: [<SearchInput placeholder={"Search by name"} accessibleName={"Search by deployment target name"} value={debouncedPartialName ?? ""} onChange={setDebouncedPartialName} autoFocus={true}/>],
        advancedFilters: {
            content: advancedFilters,
            hasUserSelectedValues: isFiltering(queryParams),
            onResetFilter: () => setQueryParams(defaultEnvironmentSummaryFilter),
        },
    };
    return (<PageContent header={{
            title: deploymentTargetsPageTitle,
            primaryAction: addDeploymentTargetPageAction,
        }} filters={filters} legacyStatus={status} callout={{ type: "custom", content: <>{tentacleUpgradesRequiredWarning}</> }}>
            <BorderedContent>
                <DeploymentTargetsSummarySection environmentsSummary={summaries} filter={queryParams} tenants={loaderData.tenants} tagIndex={loaderData.tagIndex} environments={loaderData.environments} showV2Page={showV2Page}/>
            </BorderedContent>
        </PageContent>);
}
function isFiltering(currentFilter: DeploymentTargetsPageQueryParams) {
    return !isEqual(currentFilter, defaultEnvironmentSummaryFilter);
}
